import type { CompRhythmChord, CompRhythmOptions, PlannedAttack, RhythmCell } from './compRhythm';
import type { PassKind } from './performance';

/**
 * What a chorus IS to the comp's intensity arc. Under an itinerary the form
 * says so (`passKinds`): a head is a head however many times it is played —
 * a strain written to be played twice was comped as a solo chorus when the
 * arc counted passes. Without kinds (a looped written form) the arc reads
 * position: the first pass is the head, the last of three or more hands off
 * as the out-head, and every pass between is a solo chorus.
 */
export function passRole(
  pass: number,
  passCount: number,
  kinds?: readonly PassKind[],
): 'head' | 'solo' | 'out' {
  const kind = kinds?.[pass];
  if (kind === 'head') return 'head';
  if (kind === 'solo') return 'solo';
  if (kind === 'head-out') return 'out';
  if (pass === 0) return 'head';
  return pass === passCount - 1 && passCount >= 3 ? 'out' : 'solo';
}

// A repeatable choice, with independently mixed phrase and vocabulary indices.
// Adding tiny salts to one fractional sequence ranked almost every cell in
// the same order; it produced eight identical bars despite the variation rule.
function choice(phrase: number, item: number): number {
  let n = Math.imul(phrase + 1, 0x9e3779b1) ^ Math.imul(item + 1, 0x85ebca6b);
  n = Math.imul(n ^ (n >>> 16), 0x7feb352d);
  n = Math.imul(n ^ (n >>> 15), 0x846ca68b);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

interface PhraseCell {
  start: number;
  end: number;
  cell: RhythmCell;
  phrase: number;
  level: number;
  activity: number;
}

/** Swing accompaniment: develop a figure, answer, then leave actual space.
 * These weights and limits are engineering choices, not rules attributed to
 * a particular pianist. See docs/interactive-comping-review.md.
 */
export function planSwingCompPhrases(
  chords: CompRhythmChord[],
  opts: CompRhythmOptions,
  vocabulary: RhythmCell[],
): PlannedAttack[] {
  if (!chords.length) return [];
  const meter = opts.beatsPerBar === 3 ? 3 : 4;
  const origin = opts.barOffset ?? 0;
  const bpm = Math.max(30, opts.bpm ?? 140);
  const tempo = bpm <= 88 ? 'ballad' : bpm >= 224 ? 'fast' : 'medium';
  const amount = Math.min(1, Math.max(0, opts.amount ?? 0.5));
  const end = Math.max(...chords.map((c) => c.startBeat + c.durationBeats));
  const first = Math.max(origin, chords[0].startBeat);
  // The playback form starts pass 0 at beat 0 with any pickup inside it, so a
  // pass start at or before the grid's origin names the pass the origin opens,
  // not a boundary of its own: counted separately, it made the head pass 1 and
  // comped it as a solo chorus.
  const starts = [
    ...new Set([origin, ...(opts.passStarts ?? []).filter((b) => b > origin + 1e-6)]),
  ].sort((a, b) => a - b);
  // Chord stacks count as one rhythmic onset, and overlapping/tied notes
  // form one sounding span. Polyphonic imports must not read as frantic runs.
  const notes = [...(opts.melody ?? [])].sort((a, b) => a.beat - b.beat);
  const onsets = [...new Set(notes.map((n) => n.beat))];
  const spans: { from: number; to: number }[] = [];
  for (const note of notes) {
    const previous = spans.at(-1);
    if (previous && note.beat <= previous.to + 1e-6) {
      previous.to = Math.max(previous.to, note.beat + note.durationBeats);
    } else spans.push({ from: note.beat, to: note.beat + note.durationBeats });
  }
  const onsetCount = (from: number, to: number) =>
    onsets.filter((beat) => beat >= from - 1e-6 && beat < to - 1e-6).length;
  const busyAt = (beat: number) => onsetCount(beat - 0.5, beat + 0.75) >= 2;
  const sounding = (beat: number) => spans.some((s) => beat >= s.from && beat < s.to);
  const entrances = spans.filter((s, i) => !i || s.from - spans[i - 1].to >= 1);
  const answering = (beat: number) =>
    spans.some(
      (s, i) => beat >= s.to + 0.25 && beat <= s.to + 2 && beat < (spans[i + 1]?.from ?? end),
    );
  const cells = vocabulary.filter(
    (c) =>
      (c.feels ?? ['swing']).includes('swing') &&
      (c.meters ?? [4]).includes(meter) &&
      c.tempi.includes(tempo),
  );
  const chosen: PhraseCell[] = [];
  let previous: RhythmCell | undefined;
  let repeatedBars = 0;
  let activeBars = 0;

  // Retain the global bar grid when planning a later feel segment.
  for (let start = origin + Math.floor((first - origin) / meter) * meter; start < end - 1e-6; ) {
    const bar = Math.round((start - origin) / meter);
    let pass = 0;
    starts.forEach((b, i) => {
      if (b <= start + 1e-6) pass = i;
    });
    const passBar = Math.floor((start - starts[pass]) / meter);
    const position = ((passBar % 4) + 4) % 4;
    const phrase = Math.floor(bar / 4);
    const solo = passRole(pass, starts.length, opts.passKinds) === 'solo';
    // Keep the same head-to-solo lift after lowering the ordinary 4/4 target.
    const soloLift = meter === 3 ? 0.18 : 0.25;
    const activity = Math.min(1, amount + (solo ? soloLift : 0));
    const density = onsetCount(start, start + meter) / meter;
    const target = Math.max(
      0.35,
      // Normal settings support the phrase with fewer statements. The upper
      // range still opens up: density and sustain are separate decisions.
      (meter === 3
        ? 0.5 + activity * 2.5
        : 0.5 + activity * 1.8 + Math.max(0, activity - 0.65) * 1.4) *
        (meter / 4) *
        (tempo === 'fast' ? 0.72 : tempo === 'ballad' ? 0.6 : 1) -
        (density >= 1.5 ? 0.8 : 0) -
        (position === 3 ? 0.35 : 0),
    );
    let best: RhythmCell | undefined;
    let bestScore = -Infinity;
    for (const cell of cells) {
      const stop = start + cell.bars * meter;
      if (
        cell.bars === 2 &&
        (position > 2 || stop > end + 1e-6 || stop > (starts[pass + 1] ?? Infinity))
      )
        continue;
      if (cell.temporary && (activity < 0.72 || previous?.temporary)) continue;
      if (
        !cell.attacks.length &&
        (!chosen.length || previous?.attacks.length === 0 || start + meter >= end)
      )
        continue;
      if (cell.id === previous?.id && repeatedBars + cell.bars > 4) continue;
      const attacks = cell.attacks.map((a) => start + a).filter((b) => b >= first && b < end);
      if (cell.attacks.length && !attacks.length) continue;
      let score: number;
      if (!attacks.length) {
        // Silence has its own purpose. Charging a rest for every unsounded
        // change prevented it from ever winning over fast harmonic rhythm.
        // Bass continues to state harmony while the pianist breathes.
        score = -2.2 + Math.max(0, activeBars - 2) * 0.65;
        if (position === 3) score += 1.2;
        if (density >= 1.5) score += 1.5;
      } else {
        score = 1.4 - Math.abs(attacks.length / cell.bars - target) * 1.8;
        // Avoid a stream of quick replies, including across two selected
        // cells. A deliberate busy setting can still choose these figures.
        const lastSlot = chosen.at(-1);
        const lastOffset = lastSlot?.cell.attacks.at(-1);
        const previousAttack =
          lastSlot && lastOffset !== undefined ? lastSlot.start + lastOffset : -Infinity;
        const closePairs = attacks.filter(
          (beat, i) => beat - (attacks[i - 1] ?? previousAttack) < 1,
        ).length;
        score -= (closePairs / cell.bars) * (1 - activity) * 1.6;
        const upbeatShare = cell.attacks.filter((a) => a % 1 !== 0).length / attacks.length;
        score += upbeatShare * 0.45; // syncopation is one ingredient, not the objective
        if ((!chosen.length || start + meter >= end) && attacks[0] === start) score += 1.6;
        if (cell.id === previous?.id) score += position === 0 ? -0.8 : position === 1 ? 0.65 : -0.4;
        else if (
          previous?.attacks.length === 2 &&
          cell.attacks.length === 2 &&
          cell.attacks[1] - cell.attacks[0] === previous.attacks[1] - previous.attacks[0]
        )
          score += 0.35;
        // Prefer a nearby statement or anticipation, but do not require a
        // piano hit on every chord: the rest of the band owns harmony too.
        const changes = chords.filter((c) => c.startBeat >= start && c.startBeat < stop);
        score +=
          (changes.filter((c) => attacks.some((b) => Math.abs(b - c.startBeat) <= 0.5)).length *
            0.35) /
          cell.bars;
        if (notes.length) {
          let interaction = 0;
          for (const beat of attacks) {
            if (busyAt(beat)) interaction -= 1.8;
            else if (entrances.some((s) => Math.abs(s.from - beat) < 0.25)) interaction += 0.6;
            else if (onsets.some((b) => Math.abs(b - beat) < 0.25)) interaction -= 0.6;
            else if (answering(beat)) interaction += 1.2;
            else if (sounding(beat)) interaction += 0.25;
            // An empty measure isn't a request to fill every eighth.
          }
          score += interaction / attacks.length;
        }
        // A phrase favours a small set of figures; its second half can
        // answer differently. Repeated figures keep their articulation.
        score += choice(phrase * 2 + (position >= 2 ? 1 : 0), vocabulary.indexOf(cell)) * 1.15;
      }
      if (score > bestScore) {
        best = cell;
        bestScore = score;
      }
    }
    if (!best) break;
    const stop = Math.min(end, start + best.bars * meter);
    chosen.push({
      start,
      end: stop,
      cell: best,
      phrase,
      level: (solo ? 0.82 : 0.72) + 0.16 * choice(phrase, 71),
      activity,
    });
    repeatedBars = previous?.id === best.id ? repeatedBars + best.bars : best.bars;
    activeBars = best.attacks.length ? activeBars + best.bars : 0;
    previous = best;
    start = stop;
  }

  const attacks = chosen
    .flatMap((slot) =>
      slot.cell.attacks.map((offset, index) => ({
        beat: slot.start + offset,
        index,
        slot,
      })),
    )
    .filter((a) => a.beat >= first && a.beat < end);
  const rests = chosen.filter((s) => !s.cell.attacks.length).map((s) => s.start);
  return attacks.map(({ beat, index, slot }, i) => {
    const next = attacks[i + 1]?.beat ?? end;
    const rest = rests.find((b) => b > beat) ?? end;
    const room = Math.min(next, rest, end) - beat;
    const upbeat = (beat - origin) % 1 !== 0;
    const tail = index === slot.cell.attacks.length - 1;
    const writtenDuration = slot.cell.durations?.[index];
    const shortReply =
      tail && upbeat && choice(slot.phrase, vocabulary.indexOf(slot.cell) + 97) < 0.25;
    const held =
      writtenDuration !== undefined
        ? writtenDuration >= 1
        : room >= 1 &&
          (tempo === 'ballad' ||
            slot.cell.id.includes('over') ||
            slot.cell.id === 'pad' ||
            // A busy lead asks for fewer attacks, not an automatically
            // shortened support chord. Leave the voice-led harmony sounding.
            (slot.activity <= 0.65 && (index === 0 || !shortReply)) ||
            (!busyAt(beat) &&
              tail &&
              choice(slot.phrase, vocabulary.indexOf(slot.cell) + 97) > 0.58));
    const durationBeats = Math.min(
      room,
      Math.max(
        0.05,
        Math.min(
          writtenDuration !== undefined ? room : room - 0.08,
          writtenDuration ??
            (held
              ? Math.min(slot.activity <= 0.65 || tempo === 'ballad' ? 4 : 2.5, room - 0.12)
              : upbeat
                ? 0.38
                : 0.58),
        ),
      ),
    );
    const accent = upbeat && !held;
    const velocity = slot.level * (accent ? 1.05 : tail ? 0.88 : 0.96) * (busyAt(beat) ? 0.86 : 1);
    return {
      beat,
      durationBeats,
      velocity,
      ...(accent ? { accent: true } : {}),
      // A chord is normally struck together. Sweeps are an expressive
      // choice, not an automatic consequence of a gap in the rhythm.
      rollSec: held ? 0.004 : 0.002,
    };
  });
}
